import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
  } from 'recharts';

import { useCallData } from '../../contexts/CallDataContext';
import { useWeek } from '../../contexts/WeekContext';
import { format, addDays, isWithinInterval, startOfDay, endOfDay, differenceInDays } from 'date-fns';

import { parseCustomTimestamp } from '../../utils/dateUtils';

const CallChart = ({ clientId }) => {
    const { data } = useCallData();
    const { dateRange } = useWeek(); // Changed from selectedWeek to dateRange

    // Process data to get calls per day for the selected date range
    const processCallData = () => {
        if (!dateRange || !dateRange.start || !dateRange.end) return []; // Guard against undefined
        
        // Calculate the number of days in the selected range
        const dayCount = differenceInDays(dateRange.end, dateRange.start) + 1;
        
        // Generate dates for the selected range
        const rangeDates = Array.from({ length: dayCount }, (_, i) => 
            addDays(dateRange.start, i)
        );
        
        const callsPerDay = rangeDates.reduce((acc, date) => {
            acc[format(date, 'MM/dd')] = 0;
            return acc;
        }, {});

        // Count calls for each day in the selected range
        data.forEach(call => {
            const timestampString = call?.metadata?.timestamp;
            const callDate = parseCustomTimestamp(timestampString);
            
            if (callDate) {
                const rangeStart = startOfDay(dateRange.start);
                const rangeEnd = endOfDay(dateRange.end);
                
                if (isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                    const formattedDate = format(callDate, 'MM/dd', { timeZone: 'UTC' }); 
                    if (callsPerDay.hasOwnProperty(formattedDate)) {
                        callsPerDay[formattedDate]++;
                    }
                }
            }
        });

        const chartData = rangeDates.map(date => ({
            date: format(date, 'MM/dd'),
            calls: callsPerDay[format(date, 'MM/dd')] || 0
        }));

        return chartData;
    };

    const chartData = processCallData();
    const totalCalls = chartData.reduce((sum, day) => sum + day.calls, 0);
    const maxValue = Math.max(...chartData.map(d => d.calls), 0); // Added 0 as fallback

    return (
        <div className="flex flex-col items-center card-container w-full h-[300px] space-y-2">
            <div className='flex flex-row w-full items-center justify-between'>
                <div className="text-sm text-font-primary mb-2">
                    {clientId === 'kronk' ? 'Number of Chats' : 'Number of Calls'}
                </div>
                <span className='text-lg font-semibold'>{totalCalls}</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -5 }}>
                    <XAxis dataKey="date" stroke="#b2b2b2" />
                    <YAxis stroke="#b2b2b2" />
                    <Tooltip 
                        formatter={(value, name) => {
                            const label = clientId === 'kronk' ? 'chats' : 'calls';
                            return [value, label];
                        }}
                    />
                    <Bar dataKey="calls" radius={[8, 8, 8, 8]}>
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.calls === maxValue && maxValue > 0 ? "#7067cf" : "#453bba"}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};
  
export default CallChart;
  
